"use client"

import { useCallback, useEffect, useRef, useState } from 'react'
import { LifeBuoy, LogOut, ShieldCheck, X, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useStore } from '@/lib/store'
import { payCode } from '@/lib/subscription'
import { leftLabel, type SupportStatus } from '@/lib/support'
import { useT } from '@/lib/i18n'

/**
 * 098 — the line above every page while support is in a shop.
 *
 * For the admin inside a shop: which shop, what he may do, how long is left,
 * and „დასრულება". The moment the session is gone — the owner stopped it or
 * its time ran out — his app leaves the shop by itself: the cached copy of
 * somebody else's shop must not stay on his screen.
 *
 * For the shop itself (owner, cashiers, accountant): support is in, until
 * when — and the owner's „შეწყვეტა". Asked every minute and on focus.
 */
export function SupportBanner() {
  const tr = useT()
  const session = useStore((s) => s.supportSession)
  const currentOrg = useStore((s) => s.currentOrg)
  const isOwner = useStore((s) => s.userRole === 'owner' && !s.supportSession)
  const fetchStatus = useStore((s) => s.fetchSupportStatus)
  const endSession = useStore((s) => s.endSupportSession)
  const [status, setStatus] = useState<SupportStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [, tick] = useState(0)
  const leaving = useRef(false)

  const leave = useCallback(async (end: boolean) => {
    if (leaving.current) return
    leaving.current = true
    if (end) await endSession()
    useStore.getState().clearTenantCache()
    window.location.href = '/platform'
  }, [endSession])

  const poll = useCallback(async () => {
    const st = await fetchStatus()
    if (!st) return
    setStatus(st)
    if (useStore.getState().supportSession && st.mode !== 'support') {
      toast.info(tr('support.banner.1'))
      void leave(false)
    }
  }, [fetchStatus, leave])

  useEffect(() => {
    if (!currentOrg) return
    void poll()
    const t = setInterval(() => { void poll(); tick((n) => n + 1) }, 60_000)
    const onFocus = () => { void poll() }
    window.addEventListener('focus', onFocus)
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus) }
  }, [currentOrg, poll])

  // The admin's own clock: out the minute it ends, even between polls.
  useEffect(() => {
    if (!session) return
    const ms = new Date(session.endsAt).getTime() - Date.now()
    const t = setTimeout(() => { toast.info(tr('support.banner.2')); void leave(false) }, Math.max(0, ms) + 1000)
    return () => clearTimeout(t)
  }, [session, leave])

  if (session) {
    return (
      <div className="shrink-0 rounded-2xl bg-primary text-primary-foreground px-3 py-2 sm:px-4 flex items-center gap-3 shadow-sm">
        <LifeBuoy className="size-5 shrink-0" />
        <p className="flex-1 min-w-0 text-[13px] sm:text-sm font-bold leading-snug">
          <span className="hidden sm:inline">{tr('support.banner.3')}</span>{currentOrg?.name}
          <span className="hidden sm:inline"> {payCode(currentOrg?.billing_no)} · {session.canWrite ? tr('support.banner.4') : tr('support.banner.5')}</span>
          {' · '}{tr('support.banner.6')}{leftLabel(session.endsAt)}
          <span className="hidden lg:inline font-semibold opacity-80">{tr('support.banner.7')}</span>
        </p>
        <button type="button" onClick={() => leave(true)}
          className="shrink-0 h-9 px-3.5 rounded-xl bg-primary-foreground/15 hover:bg-primary-foreground/25 text-[13px] font-bold flex items-center gap-1.5">
          <LogOut className="size-4" />{tr('support.banner.8')}
        </button>
      </div>
    )
  }

  if (status?.mode !== 'active') return null
  const stop = async () => {
    setBusy(true)
    const ok = await endSession()
    setBusy(false)
    if (ok) { toast.success(tr('support.banner.9')); void poll() }
  }
  return (
    <div className="shrink-0 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 sm:px-4 flex items-center gap-3">
      <span className="size-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0"><ShieldCheck className="size-4" /></span>
      <p className="flex-1 min-w-0 text-[13px] sm:text-sm font-bold leading-snug text-emerald-800 dark:text-emerald-300">
        {tr('support.banner.10')}{leftLabel(status.ends_at)}
        <span className="hidden sm:inline font-semibold"> · {status.can_write ? tr('support.banner.4') : tr('support.banner.5')}</span>
      </p>
      {isOwner && (
        <button type="button" onClick={stop} disabled={busy}
          className="shrink-0 h-9 px-3.5 rounded-xl border border-border bg-card hover:bg-muted/60 text-[13px] font-bold text-foreground flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}{tr('support.banner.11')}
        </button>
      )}
    </div>
  )
}
